import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useWalletAddress } from '@/hooks/useWalletAddress';
import { useAuthContext } from '@/context/AuthContext';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { TopNav } from '@/components/TopNav';
import { BottomNavigation } from '@/components/BottomNavigation';
import { HistoryDrawer } from '@/components/HistoryDrawer';
import { FaqDrawer } from '@/components/FaqDrawer';
import { PrizeTiersDrawer } from '@/components/PrizeTiersDrawer';
import { LotteryRulesDrawer } from '@/components/LotteryRulesDrawer';
import { SettingsSheet } from '@/components/SettingsSheet';
import { LotteryContent } from '@/components/LotteryContent';
import { LotteryWarmupView } from '@/components/LotteryWarmupView';
import { LotterySettlingView } from '@/components/LotterySettlingView';
import { LotteryClaimSuccessModal } from '@/components/LotteryClaimSuccessModal';
import { NFTMintingModal } from '@/components/NFTMintingModal';
import { HistoricalRewardsModal } from '@/components/HistoricalRewardsModal';
import { WinnersListDrawer } from '@/components/WinnersListDrawer';
import { TweetVerificationModal } from '@/components/TweetVerificationModal';
import {
  usePointsLotteryHistory,
  usePointsLotteryLeaderboard,
  usePointsLotterySeason,
  useUnclaimedRewards,
  useSeasonResults,
  useUserLotteryNFTs,
} from '@/hooks/usePointsLottery';
import { isCountdownEnded } from '@/utils/formatCountdown';

// ============================================================
// SEASON 0 PLACEHOLDER TOGGLE
// Set to true to show Season 0 placeholder, false to show normal flow
// ============================================================
const SHOW_SEASON_ZERO_PLACEHOLDER = false;

// ============================================================
// FORCE SETTLING VIEW TOGGLE
// Set to true to ALWAYS show "Calculating Rewards" view regardless of backend phase
// This overrides all backend state and keeps the settling view visible
// ============================================================
const FORCE_SETTLING_VIEW = false;

// New Year Campaign winners are stored under this dedicated lottery season_no
const NY_LOTTERY_SEASON_NO = 0;

// Season 0 Placeholder Component - Simple countdown view
function Season0Placeholder() {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Oct 31, 2025 10:00 AM Singapore Time (UTC+8) = Oct 31, 2025 02:00 AM UTC
  const targetDate = new Date('2025-10-31T02:00:00Z');

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const distance = targetDate.getTime() - now;

      if (distance < 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col items-center justify-center py-12 space-y-12">
      {/* Title with Golden Gradient */}
      <h1
        className="text-5xl md:text-7xl font-bold text-center"
        style={{
          fontFamily: 'Hyperspace Race Capsule, cursive',
          background: 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          textShadow: '0 0 40px rgba(254, 187, 1, 0.3)',
        }}
      >
        SEASON 1 STARTS IN
      </h1>

      {/* Countdown */}
      <div className="flex gap-4 md:gap-8">
        {[
          { label: 'DAYS', value: timeLeft.days },
          { label: 'HOURS', value: timeLeft.hours },
          { label: 'MINUTES', value: timeLeft.minutes },
          { label: 'SECONDS', value: timeLeft.seconds },
        ].map((unit) => (
          <div
            key={unit.label}
            className="flex flex-col items-center p-6 md:p-8 rounded-2xl"
            style={{
              backgroundColor: 'rgba(80, 228, 249, 0.1)',
              border: '2px solid #50E5F8',
              minWidth: '100px',
            }}
          >
            <div
              className="text-4xl md:text-6xl font-bold mb-2"
              style={{
                fontFamily: 'Outfit, sans-serif',
                color: '#FEBB01',
              }}
            >
              {String(unit.value).padStart(2, '0')}
            </div>
            <div
              className="text-sm md:text-base"
              style={{
                fontFamily: 'Hyperspace Race Capsule, cursive',
                color: '#50E5F8',
              }}
            >
              {unit.label}
            </div>
          </div>
        ))}
      </div>

      {/* Winner Trophy Image */}
      <div className="flex justify-center mt-8 px-2">
        <div className="relative w-full max-w-2xl flex items-center justify-center">
          <img
            src="/winner.svg"
            alt="Trophy"
            className="w-full h-auto object-contain"
            style={{
              filter: 'drop-shadow(0 0 20px rgba(254, 187, 1, 0.4))',
            }}
          />
        </div>
      </div>

      {/* Info Text */}
      <div
        className="text-center max-w-2xl px-4"
        style={{
          fontFamily: 'Outfit, sans-serif',
          color: 'rgba(255, 255, 255, 0.8)',
          fontSize: '16px',
          lineHeight: '1.6',
        }}
      >
        Get ready for the first season of the Points Lottery! Start earning points now by junking tokens and opening chests to climb the leaderboard when Season 1 begins.
      </div>
    </div>
  );
}

export default function Rewards() {
  const { isConnected } = useWalletAddress();
  const { isAuthenticated } = useAuthContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [historyOpen, setHistoryOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [prizeTiersOpen, setPrizeTiersOpen] = useState(false);
  const [lotteryRulesOpen, setLotteryRulesOpen] = useState(false);

  // Lottery-specific state
  const [lotteryHistoryOpen, setLotteryHistoryOpen] = useState(false);
  const [lotteryLeaderboardOpen, setLotteryLeaderboardOpen] = useState(false);
  const [historySeason, setHistorySeason] = useState<number | undefined>();
  const [claimModalOpen, setClaimModalOpen] = useState(false);

  // New NFT & Share modals
  const [nftMintingOpen, setNftMintingOpen] = useState(false);
  const [lotteryTweetVerificationOpen, setLotteryTweetVerificationOpen] = useState(false);
  const [historicalRewardsOpen, setHistoricalRewardsOpen] = useState(false);
  const [winnersListOpen, setWinnersListOpen] = useState(false);

  // Frontend countdown tracking - detect when timer ends before backend updates
  const [frontendCountdownEnded, setFrontendCountdownEnded] = useState(false);

  // Lottery hooks
  const seasonQuery = usePointsLotterySeason();
  const season = seasonQuery.data?.season;
  const seasonError = seasonQuery.error;
  const isNoSeasonError = seasonError instanceof Error &&
    seasonError.message.toLowerCase().includes('no active season');

  // Check for unclaimed rewards (only when authenticated)
  const { data: unclaimedRewardsAll } = useUnclaimedRewards();

  // NY rewards should be claimed ONLY on the NY page
  const nyUnclaimedRewardCount = useMemo(() => {
    return (unclaimedRewardsAll ?? []).filter(
      (item) =>
        item.seasonNo === NY_LOTTERY_SEASON_NO && item.unclaimedReward != null
    ).length;
  }, [unclaimedRewardsAll]);

  const unclaimedRewards = useMemo(() => {
    return (unclaimedRewardsAll ?? []).filter(
      (item) => item.seasonNo !== NY_LOTTERY_SEASON_NO
    );
  }, [unclaimedRewardsAll]);

  const hasUnclaimedItems = isConnected && unclaimedRewards.length > 0;

  const effectiveSeason = season;

  // Track frontend countdown state
  useEffect(() => {
    if (!effectiveSeason?.activeEndsAt) {
      setFrontendCountdownEnded(false);
      return;
    }

    // Check immediately
    const checkCountdown = () => {
      const ended = isCountdownEnded(effectiveSeason.activeEndsAt);
      setFrontendCountdownEnded(ended);
    };

    checkCountdown();

    // Check every second
    const interval = setInterval(checkCountdown, 1000);

    return () => clearInterval(interval);
  }, [effectiveSeason?.activeEndsAt, frontendCountdownEnded]);

  // Determine current phase
  const isWarmup = effectiveSeason?.phase === 'warmup';
  const isSettling = effectiveSeason?.phase === 'settling';
  const isComplete = effectiveSeason?.phase === 'complete';

  // Show settling view if backend phase is settling OR if frontend countdown has ended
  const isActive = effectiveSeason?.phase === 'active';
  const shouldShowSettling = isSettling || (isActive && frontendCountdownEnded);

  const seasonOptions = useMemo(() => {
    if (!effectiveSeason?.seasonNo) return [];
    
    const currentSeasonNo = effectiveSeason.seasonNo;
    const phase = effectiveSeason.phase;

    // Season is complete only if it's ended (not in warmup or active phase)
    // If phase is undefined, null, or anything other than 'warmup' or 'active', consider it complete
    const isCurrentSeasonComplete = phase !== 'warmup' && phase !== 'active';

    let seasons = [currentSeasonNo, currentSeasonNo - 1, currentSeasonNo - 2]
      .filter((value) => value > 0);

    // Remove current season if it's not complete (still in warmup or active)
    if (!isCurrentSeasonComplete) {
      seasons = seasons.filter(s => s !== currentSeasonNo);
    }

    return seasons;
  }, [effectiveSeason?.seasonNo, effectiveSeason?.phase]);

  // Set initial history season to the first available season in the filtered list
  useEffect(() => {
    if (seasonOptions.length > 0 && historySeason === undefined) {
      setHistorySeason(seasonOptions[0]);
    }
  }, [seasonOptions, historySeason]);

  // Fetch lottery history data
  const lotteryHistoryQuery = usePointsLotteryHistory({
    enabled: lotteryHistoryOpen,
    season: historySeason,
  });
  const historyData = lotteryHistoryQuery.data;
  const historyLoading = lotteryHistoryQuery.isLoading;
  const historyError = lotteryHistoryQuery.isError;

  // Deduplicate winning history to handle backend double-insertion bug
  const uniqueWinners = useMemo(() => {
    if (!historyData?.winners) return [];

    // Deduplicate by wallet + prize + amount (backend bug creates multiple records with different IDs)
    const seen = new Set<string>();
    return historyData.winners.filter(winner => {
      const key = `${winner.walletAddressMasked || 'anon'}_${winner.prizeLabel}_${winner.prizeAmountLamports}`;

      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [historyData?.winners]);

  // Fetch previous season for unclaimed winners
  // When no active season exists, we'll try to determine the last season number
  // by attempting to fetch recent season history
  const previousSeasonNo = effectiveSeason?.seasonNo 
    ? effectiveSeason.seasonNo - 1 
    : undefined;
  
  const previousSeasonQuery = usePointsLotteryHistory({
    enabled: (!!previousSeasonNo && previousSeasonNo > 0) || isNoSeasonError,
    season: previousSeasonNo || 1, // Default to 1 if no season number available
  });
  const previousSeasonData = previousSeasonQuery.data;
  const previousSeasonError = previousSeasonQuery.error;
  // Extract the actual season number from the query result (for use with APIs that need it)
  const actualPreviousSeasonNo = previousSeasonData?.season?.seasonNo ?? 0;

  // Force fresh data on mount to avoid stale cache showing wrong claim status
  useEffect(() => {
    if (previousSeasonQuery.isSuccess) {
      previousSeasonQuery.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]); // Refetch when wallet connection changes

  // Fetch lottery leaderboard
  const leaderboardQuery = usePointsLotteryLeaderboard({ enabled: lotteryLeaderboardOpen });
  const leaderboardData = leaderboardQuery.data;
  const leaderboardLoading = leaderboardQuery.isLoading;
  const leaderboardError = leaderboardQuery.isError;

  // Find unclaimed winner (only when authenticated)
  const unclaimedWinner = useMemo(() => {
    if (!isConnected || !previousSeasonData?.winners) return null;

    return previousSeasonData.winners.find(
      (winner) => winner.isCurrentUser && !winner.isClaimed
    ) || null;
  }, [isConnected, previousSeasonData?.winners, previousSeasonNo]);

  // Find any winner (claimed or unclaimed) for display purposes (only when authenticated)
  const anyWinner = useMemo(() => {
    if (!isConnected || !previousSeasonData?.winners) return null;

    // First try unclaimed, then fall back to most recent claimed winner
    if (unclaimedWinner) return unclaimedWinner;

    // Find most recently claimed winner
    const claimedWinners = previousSeasonData.winners
      .filter((winner) => winner.isCurrentUser && winner.isClaimed)
      .sort((a, b) => {
        if (!a.claimedAt || !b.claimedAt) return 0;
        return new Date(b.claimedAt).getTime() - new Date(a.claimedAt).getTime();
      });

    return claimedWinners[0] || null;
  }, [isConnected, previousSeasonData?.winners, unclaimedWinner]);

  // Fetch season results for winners list and NFT minting (when drawer/modal is opened)
  // IMPORTANT: useSeasonResults expects seasonNo (logical season number), not database ID
  const currentSeasonResults = useSeasonResults(season?.seasonNo ?? 0, winnersListOpen && isComplete);
  // Always fetch previous season results during warmup to check user participation
  const shouldFetchPreviousResults = (isWarmup && actualPreviousSeasonNo > 0 && isConnected) || (winnersListOpen && isWarmup && actualPreviousSeasonNo > 0);
  const previousSeasonResults = useSeasonResults(
    actualPreviousSeasonNo,
    shouldFetchPreviousResults
  );

  // Check if user participated in previous season (only when authenticated)
  // Uses previousSeasonResults.userResult.pointsContributed to determine participation
  const hasParticipatedInPreviousSeason = useMemo(() => {
    // Not connected or no previous season - clearly didn't participate
    if (!isConnected || !actualPreviousSeasonNo) return false;

    // If query is still loading or disabled, return false (safe default)
    if (previousSeasonResults.isLoading || !previousSeasonResults.data) return false;

    // If no userResult in the response, user didn't participate
    if (!previousSeasonResults.data.userResult) return false;

    const pointsContributed = previousSeasonResults.data.userResult.pointsContributed;
    return pointsContributed !== undefined && Number(pointsContributed) > 0;
  }, [isConnected, actualPreviousSeasonNo, previousSeasonResults.isLoading, previousSeasonResults.data]);

  // Check if user participated in current season (only when authenticated)
  const hasParticipatedInCurrentSeason = useMemo(() => {
    if (!isConnected || !seasonQuery.data?.user) return false;
    const points = seasonQuery.data.user.contributedPoints;
    return points !== undefined && Number(points) > 0;
  }, [isConnected, seasonQuery.data?.user, season?.seasonNo]);

  // Check if user has minted NFT for previous season (only when authenticated)
  const { data: userNFTs} = useUserLotteryNFTs();
  const previousSeasonNFTMinted = useMemo(() => {
    if (!isConnected || !userNFTs || !previousSeasonNo) return false;
    // Check if database has any NFT from the previous season
    return userNFTs.some(nft => nft.seasonNo === previousSeasonNo);
  }, [isConnected, userNFTs, previousSeasonNo]);

  // Check if user has minted NFT for current season (only when authenticated)
  const currentSeasonNFTMinted = useMemo(() => {
    if (!isConnected || !userNFTs || !season?.seasonNo) return false;
    // Check if database has any NFT from the current season
    return userNFTs.some(nft => nft.seasonNo === season.seasonNo);
  }, [isConnected, userNFTs, season?.seasonNo]);

  // Use appropriate results for winners list based on phase
  const seasonResults = isWarmup ? previousSeasonResults.data : currentSeasonResults.data;

  // Handler for NFT minting - NO verification required
  // All users can mint NFTs directly without sharing/verification
  const handleMintNFT = () => {
    setNftMintingOpen(true);
  };

  // Handler for claiming lottery rewards - requires tweet verification
  const handleClaimReward = () => {
    if (!unclaimedWinner) {
      if (anyWinner) {
        setClaimModalOpen(true);
      } else {
        toast.error('No rewards found');
      }
      return;
    }

    // Show verification modal for unclaimed winners unless already verified
    if (unclaimedWinner.isVerified === true && unclaimedWinner.canClaim === true) {
      setClaimModalOpen(true);
    } else {
      setLotteryTweetVerificationOpen(true);
    }
  };

  return (
    <div className="min-h-screen" style={{ position: 'relative' }}>
      <TopNav
        onHistoryClick={() => setHistoryOpen(true)}
        onFaqClick={() => setFaqOpen(true)}
        onPrizeTiersClick={() => setPrizeTiersOpen(true)}
        onSettingsClick={() => setSettingsOpen(true)}
        onNFTGalleryClick={() => navigate('/my-nft')}
      />

      {/* Unauthenticated User - Show Connect Wallet Prompt */}
      {!isConnected || !isAuthenticated ? (
        <div className="content-container-with-noise px-4 py-8 pb-24 flex items-center justify-center" style={{ overflow: 'visible' }}>
          <div className="text-center space-y-8">
            {/* Winner Trophy */}
            <div className="flex justify-center">
              <div className="relative">
                <div
                  className="absolute inset-0 rounded-full blur-3xl opacity-50"
                  style={{
                    background: 'radial-gradient(circle, #FEBB01 0%, #50E4F9 50%, transparent 70%)',
                  }}
                />
                <img
                  src="/winner.svg"
                  alt="Points Lottery"
                  className="relative w-full h-auto object-contain"
                  style={{
                    filter: 'drop-shadow(0 0 40px rgba(254, 187, 1, 0.6))',
                    maxWidth: '500px',
                    maxHeight: '500px',
                  }}
                />
              </div>
            </div>

            {/* Message */}
            <p
              className="text-2xl md:text-3xl font-semibold"
              style={{
                fontFamily: 'Outfit, sans-serif',
                color: 'rgba(255, 255, 255, 0.95)',
                lineHeight: '1.6',
              }}
            >
              Please Connect Wallet to View Rewards Page
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Side Buttons - positioned outside content area - Hidden on mobile or when no active season */}
          {!isNoSeasonError && (
        <div className="hidden md:flex fixed right-8 top-1/2 -translate-y-1/2 flex-col gap-6 z-40">
          {/* New Year Campaign Rewards button */}
          {nyUnclaimedRewardCount > 0 && (
            <button
              onClick={() => navigate('/new-year-campaign')}
              className="relative transition-all hover:opacity-70"
              aria-label="Go to New Year Campaign to claim rewards"
            >
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold text-white z-10">
                {nyUnclaimedRewardCount}
              </span>
              <div
                className="flex items-center justify-center"
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 18,
                  border: '1px solid rgba(251, 191, 36, 0.5)',
                  background:
                    'linear-gradient(135deg, #F59E0B 0%, #FBBF24 50%, #F59E0B 100%)',
                  boxShadow: '0 0 12px rgba(251, 191, 36, 0.35)',
                  fontFamily: 'Tiny5, monospace',
                }}
              >
                <span className="text-black text-sm font-bold">NY</span>
              </div>
            </button>
          )}

          {/* Historical Rewards button - show when user has unclaimed items */}
          {hasUnclaimedItems && (
            <button
              onClick={() => setHistoricalRewardsOpen(true)}
              className="relative transition-all hover:opacity-70"
            >
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold text-white z-10">
                {unclaimedRewards.length}
              </span>
              <img src="/historyrewards.svg" alt="Claim Rewards" width={72} height={72} />
            </button>
          )}
          {/* Win History and Rank buttons - hidden during warmup and settling phases */}
          {effectiveSeason?.phase !== 'warmup' && effectiveSeason?.phase !== 'settling' && (
            <>
              <button
                onClick={() => setLotteryHistoryOpen(true)}
                className="transition-all hover:opacity-70"
              >
                <img src="/winhistory.svg" alt="Win History" width={72} height={72} />
              </button>
              <button
                onClick={() => setLotteryLeaderboardOpen(true)}
                className="transition-all hover:opacity-70"
              >
                <img src="/rank.svg" alt="Leaderboard" width={72} height={72} />
              </button>
            </>
          )}
          {/* Rules button - always visible for good UX */}
          <button
            onClick={() => setLotteryRulesOpen(true)}
            className="transition-all hover:opacity-70"
          >
            <img src="/rules.svg" alt="Rules" width={72} height={72} />
          </button>
        </div>
      )}

      <div className="content-container-with-noise px-4 py-8 pb-24" style={{ overflow: 'visible' }}>
        {/* Mobile Action Buttons - Only visible on mobile */}
        {!isNoSeasonError && (
          <div className="md:hidden flex justify-center gap-3 mb-6 flex-wrap">
            {nyUnclaimedRewardCount > 0 && (
              <button
                onClick={() => navigate('/new-year-campaign')}
                className="relative transition-all hover:opacity-70"
                aria-label="Go to New Year Campaign to claim rewards"
              >
                <span className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white z-10">
                  {nyUnclaimedRewardCount}
                </span>
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 14,
                    border: '1px solid rgba(251, 191, 36, 0.5)',
                    background:
                      'linear-gradient(135deg, #F59E0B 0%, #FBBF24 50%, #F59E0B 100%)',
                    boxShadow: '0 0 10px rgba(251, 191, 36, 0.35)',
                    fontFamily: 'Tiny5, monospace',
                  }}
                >
                  <span className="text-black text-xs font-bold">NY</span>
                </div>
              </button>
            )}

            {hasUnclaimedItems && (
              <button
                onClick={() => setHistoricalRewardsOpen(true)}
                className="relative transition-all hover:opacity-70"
              >
                <span className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold text-white z-10">
                  {unclaimedRewards.length}
                </span>
                <img src="/historyrewards.svg" alt="Claim Rewards" width={56} height={56} />
              </button>
            )}
            {effectiveSeason?.phase !== 'warmup' && effectiveSeason?.phase !== 'settling' && (
              <>
                <button
                  onClick={() => setLotteryHistoryOpen(true)}
                  className="transition-all hover:opacity-70"
                >
                  <img src="/winhistory.svg" alt="Win History" width={56} height={56} />
                </button>
                <button
                  onClick={() => setLotteryLeaderboardOpen(true)}
                  className="transition-all hover:opacity-70"
                >
                  <img src="/rank.svg" alt="Leaderboard" width={56} height={56} />
                </button>
              </>
            )}
            <button
              onClick={() => setLotteryRulesOpen(true)}
              className="transition-all hover:opacity-70"
            >
              <img src="/rules.svg" alt="Rules" width={56} height={56} />
            </button>
          </div>
        )}

        <div className="max-w-6xl mx-auto space-y-6">
          {/* Lottery Content */}
          <div>
            {/* Season 0 Placeholder - Toggle with SHOW_SEASON_ZERO_PLACEHOLDER constant */}
            {SHOW_SEASON_ZERO_PLACEHOLDER ? (
              <Season0Placeholder />
            ) : FORCE_SETTLING_VIEW ? (
              /* EMERGENCY MODE: Force settling view regardless of backend state */
              <LotterySettlingView />
            ) : (
              <>
                {/* No Active Season - Show Previous Season Results or Placeholder */}
                {isNoSeasonError ? (
                  <>
                    {/* If we have previous season data with winners, show warmup view with results */}
                    {previousSeasonData?.winners && previousSeasonData.winners.length > 0 && !previousSeasonError ? (
                      <LotteryWarmupView
                        seasonNo={previousSeasonNo || 1}
                        startTime=""
                        unclaimedWinner={unclaimedWinner}
                        anyWinner={anyWinner}
                        previousSeasonNo={previousSeasonNo}
                        hasParticipatedBefore={hasParticipatedInPreviousSeason}
                        onViewWinners={() => setLotteryHistoryOpen(true)}
                        onClaimReward={handleClaimReward}
                        onMintNFT={(anyWinner || hasParticipatedInPreviousSeason) ? handleMintNFT : undefined}
                        nftAlreadyMinted={previousSeasonNFTMinted}
                      />
                    ) : (
                      /* First season ever or no previous winners - show placeholder winner UI for everyone */
                      <LotteryWarmupView
                        seasonNo={1}
                        startTime=""
                        unclaimedWinner={null}
                        anyWinner={null}
                        previousSeasonNo={undefined}
                        hasParticipatedBefore={false}
                        onViewWinners={() => setLotteryHistoryOpen(true)}
                        onClaimReward={handleClaimReward}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {/* Warmup, Settling, Complete, or Active Season */}
                    {isWarmup ? (
                      <LotteryWarmupView
                        seasonNo={season?.seasonNo ?? 0}
                        startTime={season?.warmupEndsAt ?? ''}
                        unclaimedWinner={unclaimedWinner}
                        anyWinner={anyWinner}
                        previousSeasonNo={previousSeasonNo}
                        hasParticipatedBefore={hasParticipatedInPreviousSeason}
                        onViewWinners={() => setLotteryHistoryOpen(true)}
                        onClaimReward={handleClaimReward}
                        onMintNFT={(anyWinner || hasParticipatedInPreviousSeason) ? handleMintNFT : undefined}
                        nftAlreadyMinted={previousSeasonNFTMinted}
                      />
                    ) : shouldShowSettling ? (
                      <LotterySettlingView />
                    ) : isComplete ? (
                      /* Current season complete - show warmup for completed season */
                      <LotteryWarmupView
                        seasonNo={season?.seasonNo ?? 0}
                        startTime=""
                        unclaimedWinner={unclaimedWinner}
                        anyWinner={anyWinner}
                        previousSeasonNo={season?.seasonNo}
                        hasParticipatedBefore={hasParticipatedInCurrentSeason}
                        onViewWinners={() => setLotteryHistoryOpen(true)}
                        onClaimReward={handleClaimReward}
                        onMintNFT={(anyWinner || hasParticipatedInCurrentSeason) ? handleMintNFT : undefined}
                        nftAlreadyMinted={currentSeasonNFTMinted}
                      />
                    ) : (
                      <LotteryContent />
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
        </>
      )}

      <BottomNavigation />

      {/* Drawers */}
      <HistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <FaqDrawer open={faqOpen} onClose={() => setFaqOpen(false)} />
      <PrizeTiersDrawer open={prizeTiersOpen} onClose={() => setPrizeTiersOpen(false)} />
      <LotteryRulesDrawer open={lotteryRulesOpen} onClose={() => setLotteryRulesOpen(false)} />
      <SettingsSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      {/* Lottery History Dialog */}
      <Dialog open={lotteryHistoryOpen} onOpenChange={setLotteryHistoryOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#FFFFFF' }}>
                Winning History
              </h2>
              {seasonOptions.length > 0 && (
                <select
                  value={historySeason}
                  onChange={(e) => setHistorySeason(Number(e.target.value))}
                  className="px-6 py-3 rounded-lg cursor-pointer transition-all hover:border-[#FEBB01]"
                  style={{
                    background: 'rgba(20, 20, 20, 0.95)',
                    border: '2px solid #50E4F9',
                    color: '#FFFFFF',
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: '16px',
                    fontWeight: 600,
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%2350E4F9' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 1rem center',
                    paddingRight: '3rem',
                  }}
                >
                  {seasonOptions.map((season) => (
                    <option 
                      key={season} 
                      value={season}
                      style={{
                        background: '#1a1a1a',
                        color: '#FFFFFF',
                        padding: '8px',
                      }}
                    >
                      Season {season}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {historyLoading ? (
              <div className="text-center py-8" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                Loading...
              </div>
            ) : historyError ? (
              <div className="text-center py-8" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                {historyError && typeof (historyError as any).message === 'string' && 
                 (historyError as any).message.toLowerCase().includes('no active season')
                  ? 'No season data available yet.'
                  : 'Failed to load history. Please try again later.'}
              </div>
            ) : uniqueWinners.length > 0 ? (
              <div className="space-y-2">
                {uniqueWinners.map((winner) => (
                  <div
                    key={winner.id}
                    className="p-4 rounded-lg flex items-center justify-between"
                    style={{
                      background: winner.isCurrentUser
                        ? 'rgba(80, 228, 249, 0.1)'
                        : 'rgba(52, 52, 52, 0.4)',
                      border: winner.isCurrentUser
                        ? '2px solid rgba(80, 228, 249, 0.5)'
                        : '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <div>
                      <div style={{ fontFamily: 'Outfit, sans-serif', color: '#FFFFFF', fontWeight: 600 }}>
                        {winner.isCurrentUser ? 'You' : winner.walletAddressMasked || 'Anonymous'}
                      </div>
                      <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                        {winner.prizeLabel}
                      </div>
                    </div>
                    <div className="text-right">
                      <div style={{ fontFamily: 'Outfit, sans-serif', color: '#FEBB01', fontWeight: 700 }}>
                        {(Number(winner.prizeAmountLamports) / 1e9).toFixed(4)} Credits
                      </div>
                      {winner.isCurrentUser && !winner.isClaimed && (
                        <div style={{ fontSize: '12px', color: '#EF4444', fontFamily: 'Outfit, sans-serif' }}>
                          Unclaimed
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                No winners for this season yet.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Lottery Leaderboard Dialog */}
      <Dialog open={lotteryLeaderboardOpen} onOpenChange={setLotteryLeaderboardOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <div className="space-y-4">
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#FFFFFF' }}>
              Rank Leaderboard
            </h2>

            {leaderboardLoading ? (
              <div className="text-center py-8" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                Loading...
              </div>
            ) : leaderboardError ? (
              <div className="text-center py-8" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                {leaderboardError && typeof (leaderboardError as any).message === 'string' && 
                 (leaderboardError as any).message.toLowerCase().includes('no active season')
                  ? 'No season data available yet.'
                  : 'Failed to load leaderboard. Please try again later.'}
              </div>
            ) : leaderboardData?.items && leaderboardData.items.length > 0 ? (
              <div className="space-y-2">
                {leaderboardData.items.map((entry) => (
                  <div
                    key={entry.rank}
                    className="p-4 rounded-lg flex items-center justify-between"
                    style={{
                      background: entry.isCurrentUser
                        ? 'rgba(80, 228, 249, 0.1)'
                        : 'rgba(52, 52, 52, 0.4)',
                      border: entry.isCurrentUser
                        ? '2px solid rgba(80, 228, 249, 0.5)'
                        : '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{
                          background: entry.rank <= 3 ? 'linear-gradient(135deg, #FEBB01 0%, #FF7A01 100%)' : 'rgba(255, 255, 255, 0.1)',
                          fontFamily: 'Outfit, sans-serif',
                          fontWeight: 700,
                          color: '#FFFFFF',
                        }}
                      >
                        {entry.rank}
                      </div>
                      <div>
                        <div style={{ fontFamily: 'Outfit, sans-serif', color: '#FFFFFF', fontWeight: 600 }}>
                          {entry.isCurrentUser ? 'You' : entry.walletAddress}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div style={{ fontFamily: 'Outfit, sans-serif', color: '#50E5F8', fontWeight: 700 }}>
                        {Number(entry.points).toLocaleString()} pts
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                No participants yet.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Lottery Rewards Modal */}
      {anyWinner && previousSeasonNo && (
        <LotteryClaimSuccessModal
          open={claimModalOpen}
          onClose={() => {
            setClaimModalOpen(false);
            // Refetch both current season and previous season data to clear stale cache
            queryClient.invalidateQueries({ queryKey: ['pointsLottery'] });
            queryClient.invalidateQueries({ queryKey: ['points'] });
            seasonQuery.refetch();
            previousSeasonQuery.refetch();
          }}
          reward={{
            prizeLabel: anyWinner.prizeLabel,
            prizeAmountLamports: anyWinner.prizeAmountLamports,
            seasonNo: previousSeasonNo,
          }}
        />
      )}


      {/* Tweet Verification Modal for Lottery Reward Claim - REQUIRED */}
      {/* This is MANDATORY for claiming SOL credits from lottery */}
      <TweetVerificationModal
        isOpen={lotteryTweetVerificationOpen}
        onClose={() => setLotteryTweetVerificationOpen(false)}
        type="lottery"
        winnerId={unclaimedWinner?.id}
        seasonId={previousSeasonData?.season?.id ?? season?.id}
        onSuccess={() => {
          setLotteryTweetVerificationOpen(false);
          // Invalidate lottery history to refresh winner status
          queryClient.invalidateQueries({
            queryKey: ['pointsLottery', 'history'],
          });
          // Now show claim modal
          setClaimModalOpen(true);
        }}
      />

      {/* NFT Minting Modal */}
      {/*
        Pass seasonId from previous season data (for completed seasons)
        Falls back to current season if no previous season
      */}
      <NFTMintingModal
        open={nftMintingOpen}
        onClose={() => setNftMintingOpen(false)}
        seasonId={previousSeasonData?.season?.seasonNo ?? season?.seasonNo}
        onViewGallery={() => navigate('/my-nft')}
      />


      {/* Historical Rewards Modal */}
      <HistoricalRewardsModal
        open={historicalRewardsOpen}
        onClose={() => setHistoricalRewardsOpen(false)}
        onMintSuccess={() => navigate('/my-nft')}
      />

      {/* Winners List Drawer */}
      {season && seasonResults && (
        <WinnersListDrawer
          open={winnersListOpen}
          onClose={() => setWinnersListOpen(false)}
          winners={seasonResults.winners}
          seasonNo={season.seasonNo}
        />
      )}
    </div>
  );
}
